import * as React from 'react';
import { memo, lazy, Suspense } from 'react';
import { clsx } from 'clsx';
import Dump from "../components/dump.tsx";
import { slugify } from "../helpers/utils.ts";
import type { PageSectionData } from "../rest.interface";
import { ContentRenderMode } from "./contentRenderMode";
import {
	getBgColorClass,
	getBgImageClass,
	getMarginClasses,
	getPaddingClasses,
	uniqueArray,
	responsiveHelper
} from "./renderFunctions";
import RenderItem from "./renderItem";

const VimeoIframe = lazy(() => import('../components/vimeoIframe'));

type Props = {
	section: PageSectionData
	mode?: ContentRenderMode,
	index?: number
}

function RenderSection({section, index, mode = ContentRenderMode.NORMAL}: Props) {

	let sectionClass = ['section', 'flex', 'justify-center', 'h-full', section.vimeoId && 'relative'];
	let sectionInnerClass = ['flex', 'flex-wrap', 'flex-grow', 'w-full', section.vimeoId && 'z-20 transform-gpu'];
	let itemLayoutClass = [];
	let flexClass = [];

	if (section?.maxWidth) {
		if (section?.maxWidth !== 'w-full') {
			sectionInnerClass.push('max-w-screen-' + section.maxWidth);
		}
	}

	if (section?.class) {
		sectionInnerClass.push(section.class);
	}

	if (section?.gap) {
		itemLayoutClass = itemLayoutClass.concat(responsiveHelper(section.gap, 'pl-', true));
		itemLayoutClass = itemLayoutClass.concat(responsiveHelper(section.gap, 'pr-', true));

		flexClass = flexClass.concat(responsiveHelper(section.gap, '-ml-', true));
		flexClass = flexClass.concat(responsiveHelper(section.gap, '-mr-', true));
	}

	if (section?.textAlign) {
		sectionInnerClass = sectionInnerClass.concat(responsiveHelper(section.textAlign));
	}

	if (section?.alignItems) {
		sectionInnerClass = sectionInnerClass.concat(responsiveHelper(section.alignItems));
	}

	if (section?.justifyContent) {
		sectionInnerClass = sectionInnerClass.concat(responsiveHelper(section.justifyContent));
	}

	if (section.bleedBackgroundImage) { // if image bleeds add the image style to whole section
		sectionClass = sectionClass.concat(getBgImageClass(section));
	} else {
		sectionInnerClass = sectionInnerClass.concat(getBgImageClass(section));
	}

	if (section.bleedBackgroundColor) { // if color bleeds add the color class to whole section
		sectionClass = sectionClass.concat(getBgColorClass(section));
	} else {
		sectionInnerClass = sectionInnerClass.concat(getBgColorClass(section));
	}

	if (section?.textClass) {
		sectionInnerClass.push(section.textClass);
	} else if (section.textColor) {
		sectionInnerClass.push('text-[' + section.textColor + ']');
	}

	sectionClass = sectionClass.concat(getMarginClasses(section));

	if (mode !== ContentRenderMode.REUSE_BLOCK) {
		sectionInnerClass = sectionInnerClass.concat(getPaddingClasses(section));
	}

	function getSectionId(section: PageSectionData, index: number): string {
		return (section?.id) ? slugify(section.id) : 'section' + index;
	}

	function getItemId(sectionId: string, index: number): string {
		return sectionId + '_' + index;
	}

	function renderOffsetAnchor(section: PageSectionData, index: number, mode = ContentRenderMode.NORMAL) {
		if (mode !== ContentRenderMode.SLIDER) { //&& mode !== ContentRenderMode.REUSE_SECTION) {
			if (mode === ContentRenderMode.NORMAL || section?.id) {
				return (
					<div id={getSectionId(section, index)} className="offsetAnchor"/>
				);
			}
		}
		return null;
	}

	return (<>
		{/*<Dump label="sectionClass" value={sectionClass}/>*/}
		{/*<Dump label="sectionInnerClass" value={sectionInnerClass}/>*/}
		{/*<Dump label="itemLayoutClass" value={itemLayoutClass}/>*/}
		{/*<Dump label="flexClass" value={flexClass}/>*/}

		{renderOffsetAnchor(section, index, mode)}
		<section className={clsx(sectionClass)}>
			{section.vimeoId && <>
				<div className="absolute inset-x-0 inset-y-0 bg-black/50 z-10 transform-gpu"/>
				<div className="absolute inset-x-0 inset-y-0 z-0">
					<div className="w-full h-full overflow-hidden relative z-0">
						<Suspense fallback={null}>
							<VimeoIframe
								videoId={section.vimeoId}
								autoplay={true}
								muted={true}
								loop={true}
								className="w-[177.777777vh] h-[56.25vw] min-w-full min-h-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
							/>
						</Suspense>
					</div>
				</div>
			</>}

			<div className={clsx(uniqueArray(sectionInnerClass.concat(flexClass)))}>
				{section && section.items && section.items.map((item, indexItem) =>
					<RenderItem
						key={indexItem}
						item={item}
						itemId={mode === ContentRenderMode.NORMAL ? getItemId(getSectionId(section, index), indexItem) : null}
						itemLayoutClass={itemLayoutClass}
					/>
				)}
			</div>
		</section>
	</>);
}

export default memo(RenderSection);
